'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  isThumbnailPath,
  isThumbnailSource,
  parseSupabaseStorageUrl,
  THUMBNAIL_BUCKET,
  thumbnailPath,
  thumbnailSizeFor,
  thumbnailSourcePath,
  videoPosterPaths,
  type ThumbnailSize,
} from '@flowmuse/api-core/media-shared';
import { withOssImageResize, withSupabaseImageTransform, type SupabaseImageTransformOptions } from '@/lib/utils';

/**
 * 图片展示地址的回退链：media 服务预生成的缩略图 → (可选) Supabase render 转换 → 原图。
 *
 * Supabase 转换按「每月不同原图数」计费，新图在缩略图生成前回退到转换同样会被计费一次，
 * 因此默认不走转换；仅在 NEXT_PUBLIC_SUPABASE_RENDER_FALLBACK=1 时启用。
 */
const RENDER_FALLBACK = process.env.NEXT_PUBLIC_SUPABASE_RENDER_FALLBACK === '1';

export type ImageVariantOptions = {
  /**
   * 是否尝试预生成缩略图（默认 true）。调用方已知缩略图状态时（如素材库接口返回的
   * thumbnailStatus）传 `thumbnailStatus === 'done'`，可省去对未生成缩略图的 404 探测。
   */
  thumbnail?: boolean;
};

/**
 * media 服务预生成的缩略图地址；仅对 Supabase files bucket 的公网 / render 地址生效
 * （media 服务只处理该 bucket），其它返回 undefined。
 */
export function supabaseThumbnailUrl(url: string, size: ThumbnailSize = 'lg'): string | undefined {
  if (!url) return undefined;
  const parsed = parseSupabaseStorageUrl(url);
  if (!parsed || parsed.bucket !== THUMBNAIL_BUCKET || !isThumbnailSource(parsed.path)) return undefined;
  const encoded = thumbnailPath(parsed.path, size).split('/').map(encodeURIComponent).join('/');
  return `${parsed.origin}/storage/v1/object/public/${parsed.bucket}/${encoded}`;
}

// ── 失败记录 ──
// 加载失败的地址在本会话内不再使用。缩略图在上传后异步生成，刚生成的图片首次加载常会 404，
// 因此缩略图失败后在后台用 HEAD 探测（20s、2min 各一次，兼顾补生成排队时的延迟与请求量），
// 确认已生成才恢复使用（界面不会再闪一次破图）。
const PROBE_DELAYS_MS = [20_000, 120_000];
const failedSrcs = new Set<string>();
const probing = new Set<string>();
// 已经恢复过一次的缩略图：再失败（如 HEAD 正常但图片优化服务出错）不再探测，避免「恢复 → 失败」无限循环
const recoveredOnce = new Set<string>();
// 按缩略图地址订阅恢复通知：只唤醒用到该缩略图的组件
const recoveryListeners = new Map<string, Set<() => void>>();

function subscribeRecovery(src: string, listener: () => void): () => void {
  let set = recoveryListeners.get(src);
  if (!set) recoveryListeners.set(src, (set = new Set()));
  set.add(listener);
  return () => {
    set.delete(listener);
    if (set.size === 0) recoveryListeners.delete(src);
  };
}

function normalize(src: string): string {
  try {
    return new URL(src, typeof window !== 'undefined' ? window.location.href : undefined).href;
  } catch {
    return src;
  }
}

function probeThumbnail(src: string, attempt: number) {
  if (typeof window === 'undefined' || probing.has(src) || attempt >= PROBE_DELAYS_MS.length) return;
  probing.add(src);
  window.setTimeout(async () => {
    probing.delete(src);
    try {
      const res = await fetch(src, { method: 'HEAD', cache: 'no-store' });
      if (res.ok) {
        failedSrcs.delete(src);
        recoveredOnce.add(src);
        for (const listener of recoveryListeners.get(src) ?? []) listener();
        return;
      }
    } catch {}
    probeThumbnail(src, attempt + 1);
  }, PROBE_DELAYS_MS[attempt]);
}

// ── 缺失上报 ──
// 画布等处不知道缩略图状态：缩略图 404 时把原图地址批量上报，服务端为其中已登记、从未处理过的资产
// 补派发生成任务（看到即补生成），上面的后台探测因此能在稍后切回缩略图。
const REPORT_ENDPOINT = '/api/media/thumbnails';
const REPORT_DEBOUNCE_MS = 2_000;
const REPORT_BATCH_MAX = 100;
const reportedSources = new Set<string>();
const pendingReports: string[] = [];
let reportTimer: number | undefined;

function flushReports() {
  reportTimer = undefined;
  const batch = pendingReports.splice(0, REPORT_BATCH_MAX);
  if (pendingReports.length > 0) reportTimer = window.setTimeout(flushReports, REPORT_DEBOUNCE_MS);
  if (batch.length === 0) return;
  void fetch(REPORT_ENDPOINT, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ urls: batch }),
    keepalive: true,
  }).catch(() => {});
}

function reportMissingThumbnail(thumbSrc: string) {
  if (typeof window === 'undefined') return;
  const source = thumbnailSourcePath(thumbSrc);
  if (!source || reportedSources.has(source)) return;
  reportedSources.add(source);
  pendingReports.push(source);
  reportTimer ??= window.setTimeout(flushReports, REPORT_DEBOUNCE_MS);
}

function markFailed(src: string) {
  failedSrcs.add(src);
  try {
    if (!isThumbnailPath(new URL(src).pathname)) return;
  } catch {
    return;
  }
  if (recoveredOnce.has(src)) return;
  reportMissingThumbnail(src);
  probeThumbnail(src, 0);
}

// ── 候选与回退 ──

const OSS_MIN_EDGE = 256;
const OSS_MAX_EDGE = 4096;

/**
 * OSS 托管图片按请求方框缩放的地址（非 OSS 地址返回 undefined）：
 * 宽高都给出时短边不小于方框（m_mfit，cover 不发糊）；只给一边时按该边缩放；都没给时不缩放。
 * 每边至少 256，与迁移前各处的固定宽度一致（小格子在高 DPR 下不发糊）。
 */
function ossResizedUrl(url: string, transform?: SupabaseImageTransformOptions): string | undefined {
  const { width, height } = transform ?? {};
  if (!width && !height) return undefined;
  // withOssImageResize 只处理自家 OSS 域名与路径（含 CDN 自定义域名），借它判断是否为 OSS 地址
  const probe = withOssImageResize(url, OSS_MIN_EDGE);
  if (probe === url) return undefined;
  const edge = (n: number) => Math.min(OSS_MAX_EDGE, Math.max(OSS_MIN_EDGE, Math.round(n)));
  const op =
    width && height
      ? `image/resize,m_mfit,w_${edge(width)},h_${edge(height)}`
      : width
        ? `image/resize,w_${edge(width)}`
        : `image/resize,h_${edge(height as number)}`;
  try {
    const u = new URL(probe);
    u.searchParams.set('x-oss-process', op);
    return u.toString();
  } catch {
    return undefined;
  }
}

// 浏览器（Chrome / Firefox）无法直接显示的格式：HEIC / HEIF 无法生成缩略图（sharp 预编译版不含 HEVC 解码），
// TIFF 在缩略图生成前也无法显示。这些格式保留 Supabase render 转换（只有这类文件计费，量很小）。
const BROWSER_UNSUPPORTED_RE = /\.(?:heic|heif|tiff?)$/i;

function needsServerConversion(url: string): boolean {
  const parsed = parseSupabaseStorageUrl(url);
  return Boolean(parsed && BROWSER_UNSUPPORTED_RE.test(parsed.path));
}

export function imageVariantCandidates(
  url: string,
  transform?: SupabaseImageTransformOptions,
  options?: ImageVariantOptions,
): string[] {
  if (!url) return [];
  const longest = Math.max(transform?.width ?? 0, transform?.height ?? 0);
  // 超过最大档能提供的尺寸时不用缩略图（避免放大发糊）
  const size = thumbnailSizeFor(longest);
  const list: Array<string | undefined> = [];
  if (options?.thumbnail !== false && size) list.push(supabaseThumbnailUrl(url, size));
  list.push(ossResizedUrl(url, transform));
  if (needsServerConversion(url)) list.push(withSupabaseImageTransform(url, transform ?? {}));
  else if (RENDER_FALLBACK && transform) list.push(withSupabaseImageTransform(url, transform));
  list.push(url);
  return [...new Set(list.filter((s): s is string => Boolean(s)))];
}

/** 当前应展示的地址：第一个未失败的候选；原图永远兜底。 */
export function imageVariantSrc(
  url: string,
  transform?: SupabaseImageTransformOptions,
  options?: ImageVariantOptions,
): string {
  return firstAvailable(imageVariantCandidates(url, transform, options), url);
}

function firstAvailable(candidates: string[], url: string): string {
  return candidates.find((c) => !failedSrcs.has(normalize(c))) ?? url;
}

/** 将失败地址记入缓存，返回下一个候选（已是原图时返回 undefined）。组件请用 useImageVariant / <VariantImage>。 */
export function nextImageVariant(
  failedSrc: string,
  url: string,
  transform?: SupabaseImageTransformOptions,
  options?: ImageVariantOptions,
): string | undefined {
  const failed = normalize(failedSrc);
  if (failed === normalize(url)) return undefined;
  markFailed(failed);
  const next = imageVariantSrc(url, transform, options);
  return normalize(next) === failed ? undefined : next;
}

/** 是否为 Supabase 存储（含自定义域名）或 OSS 上的图片：这类地址在页面上总会经过缩略图 / 图片处理再显示。 */
export function isManagedImageUrl(url: string): boolean {
  if (!url) return false;
  return parseSupabaseStorageUrl(url) !== null || withOssImageResize(url, 1) !== url;
}

/**
 * 是否为 media 服务预生成的缩略图地址。缩略图本身已足够小，应直接加载（跳过 Next 图片优化）：
 * 避免服务端重复处理，也让缩略图自身的缓存头生效（Next 优化结果会按 minimumCacheTTL 缓存一年）。
 */
export function isPregeneratedThumbnailUrl(src: string): boolean {
  const parsed = parseSupabaseStorageUrl(src);
  return Boolean(parsed && isThumbnailPath(parsed.path));
}

/**
 * next/image 是否应跳过 Next 图片优化：预生成缩略图本身已足够小，直接加载；
 * Supabase 上的其它地址（原图、render 转换）交给 Next 优化缩小（与改造前 render 地址的处理一致）；
 * 其它域名维持直接加载。
 */
export function shouldSkipNextImageOptimization(src: string): boolean {
  return !parseSupabaseStorageUrl(src) || isPregeneratedThumbnailUrl(src);
}

// ── 素材库资产（接口返回 thumbnailStatus）──

/** 资产缩略图的来源：图片为原图，视频为首帧原图（无法推导首帧时为 undefined）。 */
export function assetThumbnailSource(type: string, rawSrc: string): string | undefined {
  if (type === 'video') return videoPosterPaths(rawSrc)?.first;
  return rawSrc;
}

const DOWNLOAD_THUMB_HEIGHT = 512;

// 下载多张时可能先弹出确认框，blob 地址保留久一些再释放
const DOWNLOAD_URL_TTL_MS = 10 * 60_000;

/**
 * 「下载缩略图」：生成高 512（不放大）的 PNG，返回 blob: 地址（约 10 分钟后自动释放）。
 * 源图优先用预生成缩略图（xl 档，短边 1024 足够），未就绪时用原图，均在浏览器内缩放，
 * 不产生 Supabase 转换费用。调用方下载时需指定 type 'image/png'（blob 地址不带扩展名）。
 * 任何一步失败时返回可直接下载的地址（缩略图或原图）。多张请用 resolveThumbnailDownloadUrls。
 */
export async function resolveThumbnailDownloadUrl(url: string): Promise<string> {
  const thumb = supabaseThumbnailUrl(url, 'xl');
  let source = url;
  if (thumb) {
    try {
      if ((await fetch(thumb, { method: 'HEAD' })).ok) source = thumb;
    } catch {}
  }
  try {
    const bitmap = await createImageBitmap(await (await fetch(source)).blob());
    const scale = Math.min(1, DOWNLOAD_THUMB_HEIGHT / bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const png = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
    // 及时释放画布像素内存
    canvas.width = 0;
    canvas.height = 0;
    if (!png) return source;
    const objectUrl = URL.createObjectURL(png);
    window.setTimeout(() => URL.revokeObjectURL(objectUrl), DOWNLOAD_URL_TTL_MS);
    return objectUrl;
  } catch {
    return source;
  }
}

/** 多张「下载缩略图」：逐张处理，避免同时下载、解码多张原图造成内存峰值。 */
export async function resolveThumbnailDownloadUrls(urls: string[]): Promise<string[]> {
  const result: string[] = [];
  for (const url of urls) result.push(await resolveThumbnailDownloadUrl(url));
  return result;
}

/**
 * 图片展示的统一入口（列表项请用 components/variant-image.tsx 的 <VariantImage>）：
 * - src：当前应展示的地址（缩略图 → 可选 render → 原图）；
 * - onError：挂到 <img> / next/image 上，失败时切到下一个候选；
 * - exhausted：原图也加载失败，调用方据此显示占位图标；
 * 缩略图失败后由后台确认生成，确认后只重新渲染用到它的组件。
 */
export function useImageVariant(
  url: string | undefined,
  transform?: SupabaseImageTransformOptions,
  options?: ImageVariantOptions & {
    /** 变化时清除「原图也失败」状态并重新尝试（如视频节点切换视频源而首帧地址不变） */
    resetKey?: string;
  },
) {
  const [, setVersion] = useState(0);
  const [exhaustedFor, setExhaustedFor] = useState<string | null>(null);
  // 调用方常传内联对象：按字段稳定化，避免每次渲染都换引用
  const hasTransform = transform !== undefined;
  const { width, height, resize, quality, format } = transform ?? {};
  const stableTransform = useMemo<SupabaseImageTransformOptions | undefined>(
    () => (hasTransform ? { width, height, resize, quality, format } : undefined),
    [hasTransform, width, height, resize, quality, format],
  );
  const thumbnail = options?.thumbnail;
  const stableOptions = useMemo<ImageVariantOptions | undefined>(
    () => (thumbnail === undefined ? undefined : { thumbnail }),
    [thumbnail],
  );
  // 候选列表只在输入变化时计算一次；src 每次渲染按失败记录从中选取
  const candidates = useMemo(
    () => (url ? imageVariantCandidates(url, stableTransform, stableOptions) : []),
    [url, stableTransform, stableOptions],
  );
  const src = url ? firstAvailable(candidates, url) : undefined;
  // 首选候选（缩略图）；它失败后台确认生成时，只重新渲染本组件
  const preferred = candidates[0];

  useEffect(() => {
    if (!preferred) return;
    return subscribeRecovery(normalize(preferred), () => {
      // 缩略图确认已生成：清除「全部失败」状态，重新显示图片
      setExhaustedFor(null);
      setVersion((n) => n + 1);
    });
  }, [preferred]);

  // 「全部失败」状态按地址 + 重置键 + 候选列表记录：候选变化（如接口返回缩略图已就绪）时自动重新尝试
  const exhaustedKey =
    url === undefined ? null : [url, options?.resetKey ?? '', candidates.join('\n')].join('\u0000');
  const onError = useCallback(() => {
    if (!url || !src) return;
    if (nextImageVariant(src, url, stableTransform, stableOptions)) setVersion((n) => n + 1);
    else setExhaustedFor(exhaustedKey);
  }, [url, src, stableTransform, stableOptions, exhaustedKey]);

  return { src, onError, exhausted: exhaustedKey !== null && exhaustedFor === exhaustedKey };
}
